import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { OpacitySlider } from "../components/OpacitySlider";
import { IssueList } from "../components/IssueList";
import { useRecord, useRecords, useSessionResult } from "../queries/useRecords";
import { cn } from "../lib/utils";

type ViewMode = "overlay" | "template" | "user";

const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='#f5f5f4'/><text x='100' y='115' font-size='24' text-anchor='middle' fill='#a8a29e' font-family='serif'>暂无图片</text></svg>`,
  );

export function OverlayPage() {
  const { recordId = "" } = useParams<{ recordId: string }>();
  const recordsQuery = useRecords();
  const { record } = useRecord(recordId);

  // Once we know the parent sessionId, fetch the full analyze payload so we
  // can draw the cropped per-character image from the original photo.
  const sessionQuery = useSessionResult(record?.sessionId);

  const [opacity, setOpacity] = useState(50);
  const [mode, setMode] = useState<ViewMode>("overlay");

  if (recordsQuery.isLoading) {
    return <div className="text-sm text-[color:var(--text-muted)]">加载中…</div>;
  }

  if (!record) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="text-xl font-semibold">未找到该记录</h1>
        <p className="text-sm text-[color:var(--text-muted)]">
          记录 ID：<code className="rounded bg-[color:var(--surface-2)] px-1.5 py-0.5">{recordId}</code>
          ，可能尚未同步。
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => recordsQuery.refetch()}>刷新</Button>
        </div>
      </div>
    );
  }

  // Pick the bbox: prefer the one in the analyze payload, fall back to bbox
  // stored on the record itself.
  const charFromSession = sessionQuery.data?.characters.find((c) => c.recordId === recordId);
  const bbox = charFromSession?.bbox ?? record.bbox;
  const fullImageUrl = sessionQuery.data?.imageUrl ?? record.imageUrl;
  const templateSrc = charFromSession?.standardTemplateUrl ?? record.standardTemplateUrl ?? PLACEHOLDER;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Link
          to={record.sessionId ? `/result/${record.sessionId}` : "/history"}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--text-muted)] underline-offset-4 hover:text-[color:var(--text)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          返回结果
        </Link>
        <span className="font-serif text-2xl">{record.character}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>叠加对比 · 单字</CardTitle>
          <SegmentedView value={mode} onChange={setMode} />
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-md border border-[color:var(--border)] bg-[color:var(--surface-2)]">
            {bbox && fullImageUrl ? (
              <CroppedCharImage
                imageUrl={fullImageUrl}
                bbox={bbox}
                templateSrc={templateSrc}
                opacity={opacity}
                mode={mode}
              />
            ) : (
              <>
                <img
                  src={templateSrc}
                  alt="标准模板"
                  className={cn(
                    "absolute inset-0 h-full w-full object-contain transition-opacity",
                    mode === "user" ? "opacity-0" : "opacity-100",
                  )}
                />
                <img
                  src={record.imageUrl ?? PLACEHOLDER}
                  alt="用户书写"
                  className={cn(
                    "absolute inset-0 h-full w-full object-contain transition-opacity",
                    mode === "template" ? "opacity-0" : "opacity-100",
                  )}
                  style={mode === "overlay" ? { opacity: opacity / 100 } : undefined}
                />
              </>
            )}
          </div>

          {mode === "overlay" && (
            <OpacitySlider value={opacity} onChange={setOpacity} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>问题与建议</CardTitle>
        </CardHeader>
        <CardContent>
          <IssueList issues={record.issues ?? []} />
        </CardContent>
      </Card>
    </div>
  );
}

/** Render the user's character (cropped from the full session image via a
 *  <canvas>) under the standard template, with the opacity slider blending
 *  the two. */
function CroppedCharImage({
  imageUrl,
  bbox,
  templateSrc,
  opacity,
  mode,
}: {
  imageUrl: string;
  bbox: { x: number; y: number; w: number; h: number };
  templateSrc: string;
  opacity: number;
  mode: ViewMode;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      // Pad the crop a little so the full glyph fits in frame.
      const pad = Math.max(bbox.w, bbox.h) * 0.1;
      const sx = Math.max(0, bbox.x - pad);
      const sy = Math.max(0, bbox.y - pad);
      const sw = Math.min(img.naturalWidth - sx, bbox.w + pad * 2);
      const sh = Math.min(img.naturalHeight - sy, bbox.h + pad * 2);
      if (sw <= 0 || sh <= 0) {
        setDataUrl(null);
        return;
      }
      const size = Math.max(sw, sh);
      const canvas = canvasRef.current ?? document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setDataUrl(null);
        return;
      }
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, sx, sy, sw, sh, (size - sw) / 2, (size - sh) / 2, sw, sh);
      try {
        setDataUrl(canvas.toDataURL("image/png"));
      } catch {
        setDataUrl(null);
      }
    };
    img.onerror = () => {
      if (!cancelled) setDataUrl(null);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl, bbox.x, bbox.y, bbox.w, bbox.h]);

  const userSrc = dataUrl ?? PLACEHOLDER;

  return (
    <>
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
      <img
        src={templateSrc}
        alt="标准模板"
        className={cn(
          "absolute inset-0 h-full w-full object-contain transition-opacity",
          mode === "user" ? "opacity-0" : "opacity-100",
        )}
      />
      <img
        src={userSrc}
        alt="用户书写"
        className={cn(
          "absolute inset-0 h-full w-full object-contain transition-opacity",
          mode === "template"
            ? "opacity-0"
            : mode === "user"
              ? "opacity-100"
              : opacity / 100,
        )}
        style={mode === "overlay" ? { opacity: opacity / 100 } : undefined}
      />
    </>
  );
}

function SegmentedView({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const opts: Array<{ key: ViewMode; label: string }> = [
    { key: "overlay", label: "叠加" },
    { key: "template", label: "仅标准" },
    { key: "user", label: "仅用户" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="视图模式"
      className="inline-flex items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] p-1 text-xs"
    >
      {opts.map((opt) => {
        const selected = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.key)}
            className={cn(
              "rounded-full px-3 py-1 transition-colors",
              selected
                ? "bg-[color:var(--primary)] text-[color:var(--bg)]"
                : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}